import React from 'react';
import {useTranslation} from 'react-i18next';
import {WideHeader} from '../../layout/wide';

interface CollectionHeaderProps {
  title: string;
  introduction?: string;
  introductionHtml?: string;
  range: string;
  totalCount: number;
  zoomedIn: boolean;
  withDescriptions: boolean;
  columnsCount: number;
  onToggleZoom: (zoomedIn: boolean) => void;
  onToggleDescriptions: (withDescriptions: boolean) => void;
  onChangeColumns: (columns: number) => void;
}

interface GalleryActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: string;
  title: string;
}

const GalleryActionButton: React.FC<GalleryActionButtonProps> = ({
  onClick,
  disabled = false,
  icon,
  title
}) => {
  return (
    <button
      className="bg-gray-100 hover:bg-gray-200 p-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-8 h-10"
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      <i className={`text-gray-700 fa ${icon}`} />
    </button>
  );
};

const GalleryHeader: React.FC<CollectionHeaderProps> = ({
                                                          title,
                                                          introduction,
                                                          introductionHtml,
                                                          range,
                                                          totalCount,
                                                          zoomedIn,
                                                          withDescriptions,
                                                          columnsCount,
                                                          onToggleZoom,
                                                          onToggleDescriptions,
                                                          onChangeColumns
                                                        }) => {
  const {t} = useTranslation();
  const MIN_COLUMNS = 2;
  const MAX_COLUMNS = 8;

  const actions = (
    <>
      <GalleryActionButton
        onClick={() => onToggleDescriptions(!withDescriptions)}
        icon={withDescriptions ? 'fa-file' : 'fa-file-lines'}
        title={withDescriptions ? t('gallery.header.actions.hideDescriptions') : t('gallery.header.actions.showDescriptions')}
      />
      <GalleryActionButton
        onClick={() => onToggleZoom(!zoomedIn)}
        icon={zoomedIn ? 'fa-compress' : 'fa-expand'}
        title={zoomedIn ? t('gallery.header.actions.zoomOut') : t('gallery.header.actions.zoomIn')}
      />
      <GalleryActionButton
        onClick={() => onChangeColumns(Math.min(MAX_COLUMNS, columnsCount + 2))}
        disabled={columnsCount === MAX_COLUMNS}
        icon="fa-table-cells"
        title={t('gallery.header.actions.moreColumns')}
      />
      <GalleryActionButton
        onClick={() => onChangeColumns(Math.max(MIN_COLUMNS, columnsCount - 2))}
        disabled={columnsCount === MIN_COLUMNS}
        icon="fa-table-cells-large"
        title={t('gallery.header.actions.fewerColumns')}
      />
    </>
  );

  return (
    <WideHeader
      title={title}
      introduction={introduction}
      introductionHtml={introductionHtml}
      caption={t('gallery.header.showingResults', {range, totalCount})}
      actions={actions}
    />
  );
};

export default GalleryHeader;
